import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import TemplateBuilder from '../components/TemplateBuilder';
import { Users, Database, CreditCard, Settings, RefreshCw, Shield, Loader2, Mail, Cloud, Clock, Play, History, CheckCircle, XCircle, AlertTriangle, Link2, Unlink, TestTube, Building2, FileText, ShieldAlert, Activity, Lock, Eye, Megaphone, Download, MapPin, ChevronDown, ChevronUp, Bug } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

export default function AdminPage() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newTier, setNewTier] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncLogsLoading, setSyncLogsLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [waitlistStats, setWaitlistStats] = useState(null);
  
  // CRM Connections state
  const [crmConnections, setCrmConnections] = useState([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(null);
  const [syncingConnection, setSyncingConnection] = useState(null);
  
  // Security/Audit state
  const [auditStats, setAuditStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsOpen, setAuditLogsOpen] = useState(false);
  
  // User Sign-On Log state
  const [signOnLogs, setSignOnLogs] = useState([]);
  const [signOnLogsLoading, setSignOnLogsLoading] = useState(false);
  const [signOnFilter, setSignOnFilter] = useState('all'); // all, success, failed
  
  // Scheduler Health state
  const [schedulerHealth, setSchedulerHealth] = useState(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  
  // Collapsible sections state
  const [syncHistoryOpen, setSyncHistoryOpen] = useState(false);
  const [crmConnectionsOpen, setCrmConnectionsOpen] = useState(false);
  const [teamsOpen, setTeamsOpen] = useState(false);
  
  // Teams management state
  const [teamsStats, setTeamsStats] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  
  // Error/Fix Log state
  const [errorLogOpen, setErrorLogOpen] = useState(false);
  const [errorLogContent, setErrorLogContent] = useState(null);
  const [errorLogLoading, setErrorLogLoading] = useState(false);
  
  // Check if user is JH (sales tools access)
  const isSalesAdmin = user?.email === 'jh@insite360.com';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, usersRes, settingsRes, logsRes, crmRes, auditStatsRes, waitlistRes] = await Promise.all([
          api().get('/admin/stats'),
          api().get('/admin/users'),
          api().get('/admin/settings'),
          api().get('/admin/sync-logs?limit=10'),
          api().get('/admin/crm/connections'),
          api().get('/admin/audit-logs/stats').catch(() => ({ data: null })),
          api().get('/waitlist/stats').catch(() => ({ data: null }))
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data.users);
        setSettings(settingsRes.data);
        setSyncLogs(logsRes.data.logs || []);
        setCrmConnections(crmRes.data.connections || []);
        setAuditStats(auditStatsRes.data);
        setWaitlistStats(waitlistRes.data);
      } catch (err) {
      console.error('Failed to fetch admin data:', err);
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Failed to load admin data';
      toast.error(errorMsg);
    } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);
  
  const fetchAuditLogs = async () => {
    try {
      const res = await api().get('/admin/audit-logs?limit=20');
      setAuditLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    }
  };
  
  // Fetch user sign-on logs
  const fetchSignOnLogs = async (filter = 'all') => {
    setSignOnLogsLoading(true);
    try {
      let eventTypes = '';
      if (filter === 'success') {
        eventTypes = '&event_type=LOGIN_SUCCESS';
      } else if (filter === 'failed') {
        eventTypes = '&event_type=LOGIN_FAILED,LOGIN_BLOCKED';
      } else {
        eventTypes = '&event_type=LOGIN_SUCCESS,LOGIN_FAILED,LOGIN_BLOCKED';
      }
      const res = await api().get(`/admin/audit-logs?limit=50${eventTypes}`);
      setSignOnLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch sign-on logs:', err);
    } finally {
      setSignOnLogsLoading(false);
    }
  };
  
  // Fetch scheduler health
  const fetchSchedulerHealth = async () => {
    setSchedulerLoading(true);
    try {
      const res = await api().get('/admin/scheduler-health');
      setSchedulerHealth(res.data);
    } catch (err) {
      console.error('Failed to fetch scheduler health:', err);
      toast.error('Failed to fetch scheduler health');
    } finally {
      setSchedulerLoading(false);
    }
  };
  
  // Fetch sign-on logs on mount
  useEffect(() => {
    fetchSignOnLogs(signOnFilter);
  }, [signOnFilter]);
  
  // Fetch scheduler health on mount
  useEffect(() => {
    fetchSchedulerHealth();
  }, []);
  
  // Fetch teams data
  const fetchTeamsData = async () => {
    setTeamsLoading(true);
    try {
      const [statsRes, teamsRes] = await Promise.all([
        api().get('/teams/admin/stats'),
        api().get('/teams/admin/all?limit=20')
      ]);
      setTeamsStats(statsRes.data);
      setAllTeams(teamsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch teams data:', err);
    } finally {
      setTeamsLoading(false);
    }
  };
  
  // Fetch teams when section is opened
  useEffect(() => {
    if (teamsOpen && !teamsStats) {
      fetchTeamsData();
    }
  }, [teamsOpen]);
  
  // Fetch error/fix log
  const fetchErrorLog = async () => {
    setErrorLogLoading(true);
    try {
      const res = await api().get('/admin/error-fix-log');
      setErrorLogContent(res.data);
    } catch (err) {
      console.error('Failed to fetch error/fix log:', err);
      toast.error('Failed to load error/fix log');
    } finally {
      setErrorLogLoading(false);
    }
  };
  
  // Fetch error log when section is opened
  useEffect(() => {
    if (errorLogOpen && !errorLogContent) {
      fetchErrorLog();
    }
  }, [errorLogOpen]);

  const handleUpdateTier = async () => {
    if (!selectedUser || !newTier) return;
    try {
      await api().put(`/admin/users/${selectedUser.id}/tier?tier=${newTier}`);
      toast.success('User tier updated!');
      setUserDialogOpen(false);
      // Refresh users
      const res = await api().get('/admin/users');
      setUsers(res.data.users);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Failed to update user tier';
      toast.error(errorMsg);
    }
  };

  const handleUpdateSettings = async (key, value) => {
    try {
      await api().put('/admin/settings', { [key]: value });
      toast.success('Settings updated!');
      setSettings(prev => ({ ...prev, [key]: value }));
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Failed to update settings';
      toast.error(errorMsg);
    }
  };

  const handleTriggerSync = async () => {
    setSyncLoading(true);
    try {
      await api().post('/admin/trigger-sync');
      toast.success('Data sync triggered!');
      // Refresh settings to get new sync status
      const res = await api().get('/admin/settings');
      setSettings(res.data);
      // Wait a bit and refresh logs
      setTimeout(async () => {
        const logsRes = await api().get('/admin/sync-logs?limit=10');
        setSyncLogs(logsRes.data.logs || []);
      }, 5000);
    } catch (err) {
      toast.error('Failed to trigger sync');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleRefreshLogs = async () => {
    setSyncLogsLoading(true);
    try {
      const logsRes = await api().get('/admin/sync-logs?limit=10');
      setSyncLogs(logsRes.data.logs || []);
      toast.success('Sync logs refreshed');
    } catch (err) {
      toast.error('Failed to refresh logs');
    } finally {
      setSyncLogsLoading(false);
    }
  };

  const handleViewLogDetails = async (logId) => {
    try {
      const res = await api().get(`/admin/sync-logs/${logId}`);
      setSelectedLog(res.data);
      setLogDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load log details');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setTestEmailLoading(true);
    try {
      await api().post(`/admin/send-test-email?email=${encodeURIComponent(testEmail)}`);
      toast.success(`Test email sent to ${testEmail}`);
    } catch (err) {
      toast.error('Failed to send test email. Check SendGrid configuration.');
    } finally {
      setTestEmailLoading(false);
    }
  };

  // CRM Connection handlers
  const handleRefreshCrmConnections = async () => {
    setCrmLoading(true);
    try {
      const res = await api().get('/admin/crm/connections');
      setCrmConnections(res.data.connections || []);
      toast.success('CRM connections refreshed');
    } catch (err) {
      toast.error('Failed to refresh CRM connections');
    } finally {
      setCrmLoading(false);
    }
  };

  const handleTestCrmConnection = async (connectionId) => {
    setTestingConnection(connectionId);
    try {
      const res = await api().post(`/admin/crm/test/${connectionId}`);
      toast.success(res.data.message);
      // Refresh connections to show updated status
      const crmRes = await api().get('/admin/crm/connections');
      setCrmConnections(crmRes.data.connections || []);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Connection test failed');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleSyncCrmConnection = async (connectionId) => {
    setSyncingConnection(connectionId);
    try {
      const res = await api().post(`/admin/crm/sync/${connectionId}`);
      toast.success(res.data.message);
      // Refresh connections to show updated sync time
      const crmRes = await api().get('/admin/crm/connections');
      setCrmConnections(crmRes.data.connections || []);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Sync failed');
    } finally {
      setSyncingConnection(null);
    }
  };

  const handleDeleteCrmConnection = async (connectionId, crmType) => {
    if (!window.confirm(`Are you sure you want to delete this ${crmType} connection?`)) {
      return;
    }
    try {
      await api().delete(`/admin/crm/connections/${connectionId}`);
      toast.success('CRM connection deleted');
      setCrmConnections(prev => prev.filter(c => c.id !== connectionId));
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to delete connection');
    }
  };

  const getCrmIcon = (type) => {
    if (type === 'salesforce') return '☁️';
    if (type === 'dynamics') return '🔷';
    if (type === 'dubsado') return '💜';
    return '🔗';
  };

  const getCrmStatusColor = (status) => {
    switch(status) {
      case 'connected': return 'text-emerald-400';
      case 'configured': return 'text-amber-400';
      case 'error': return 'text-red-400';
      default: return 'text-zinc-400';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/20' },
    { label: 'Paid Users', value: stats?.paid_users || 0, icon: CreditCard, color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
    { label: 'Active Watchlists', value: stats?.active_watchlists || 0, icon: Database, color: 'text-amber-400', bg: 'bg-amber-500/20' },
    { label: 'City Waitlist', value: waitlistStats?.total_signups || 0, icon: MapPin, color: 'text-purple-400', bg: 'bg-purple-500/20' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-indigo-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <p className="text-zinc-400">Manage users, settings, and data sync</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="glass-card p-6 rounded-xl">
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <p className="text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-zinc-400">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* City Waitlist Breakdown - Only show if there are signups */}
        {waitlistStats?.by_city?.length > 0 && (
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-semibold text-white">City Waitlist Breakdown</h2>
              <span className="ml-auto text-sm text-zinc-400">{waitlistStats.total_signups} total signups</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {waitlistStats.by_city.map((city) => (
                <div key={city.city_id} className="p-3 bg-white/5 rounded-lg border border-white/5">
                  <p className="text-lg font-bold text-white">{city.waitlist_count}</p>
                  <p className="text-xs text-zinc-400">{city.city_name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin Settings */}
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <Settings className="w-5 h-5 text-indigo-400" />
            <h2 className="text-lg font-semibold text-white">System Settings</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Data Sync Frequency */}
            <div>
              <Label className="text-zinc-300">Data Sync Frequency</Label>
              <div className="flex gap-2 mt-1">
                <Select 
                  value={String(settings?.data_sync_frequency || 24)}
                  onValueChange={(v) => handleUpdateSettings('data_sync_frequency', parseInt(v))}
                >
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Every Hour</SelectItem>
                    <SelectItem value="6">Every 6 Hours</SelectItem>
                    <SelectItem value="12">Every 12 Hours</SelectItem>
                    <SelectItem value="24">Daily</SelectItem>
                    <SelectItem value="48">Every 2 Days</SelectItem>
                    <SelectItem value="168">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-zinc-500 mt-1">How often to check NYC Open Data for updates</p>
            </div>

            {/* Max Properties */}
            <div>
              <Label className="text-zinc-300">Max Properties Per User</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number"
                  value={settings?.max_properties_per_user || 100}
                  onChange={(e) => setSettings(prev => ({ ...prev, max_properties_per_user: parseInt(e.target.value) }))}
                  className="input-dark"
                  min={10}
                />
                <Button
                  onClick={() => handleUpdateSettings('max_properties_per_user', settings?.max_properties_per_user)}
                  className="btn-secondary"
                >
                  Save
                </Button>
              </div>
            </div>

            {/* Live NYC Data Toggle */}
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Cloud className="w-5 h-5 text-indigo-400" />
                <div>
                  <p className="text-white font-medium">Live NYC Open Data</p>
                  <p className="text-xs text-zinc-500">Fetch real-time data from NYC APIs</p>
                </div>
              </div>
              <Switch
                checked={settings?.use_live_nyc_data || false}
                onCheckedChange={(v) => handleUpdateSettings('use_live_nyc_data', v)}
              />
            </div>

            {/* Email Notifications Toggle */}
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-white font-medium">Email Notifications</p>
                  <p className="text-xs text-zinc-500">Send violation alerts to users</p>
                </div>
              </div>
              <Switch
                checked={settings?.email_notifications_enabled ?? true}
                onCheckedChange={(v) => handleUpdateSettings('email_notifications_enabled', v)}
              />
            </div>
          </div>

          {/* Sync Status & Actions */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-400">Last Sync:</span>
                <span className="text-white">
                  {settings?.last_sync_time 
                    ? new Date(settings.last_sync_time).toLocaleString()
                    : 'Never'}
                </span>
                {settings?.sync_status && (
                  <span className={`badge-${settings.sync_status === 'running' ? 'warning' : settings.sync_status === 'error' ? 'error' : 'info'}`}>
                    {settings.sync_status}
                  </span>
                )}
              </div>
              <Button
                onClick={handleTriggerSync}
                disabled={syncLoading}
                className="btn-secondary"
              >
                {syncLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Trigger Sync Now
              </Button>
            </div>
          </div>
        </div>

        {/* Per-Dataset Sync Configuration */}
        <div className="glass-card rounded-xl p-6" data-testid="dataset-sync-config">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-indigo-400" />
              <h2 className="text-lg font-semibold text-white">Dataset Sync Schedule</h2>
            </div>
            <p className="text-xs text-zinc-500">Configure how often each dataset refreshes</p>
          </div>

          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Dataset</th>
                  <th>Description</th>
                  <th>Sync Frequency</th>
                  <th>Status</th>
                  <th>Last Sync</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { id: 'dob_violations', name: 'DOB Violations', description: 'Department of Buildings violations', icon: '🏢', color: 'indigo' },
                  { id: 'ecb_violations', name: 'ECB Violations', description: 'Environmental Control Board violations', icon: '⚠️', color: 'amber' },
                  { id: 'fire_violations', name: 'Fire Violations', description: 'Fire Department violations', icon: '🔥', color: 'rose' },
                  { id: 'permits', name: 'DOB Permits', description: 'Building permit applications', icon: '📋', color: 'emerald' },
                  { id: 'dob_now', name: 'DOB NOW Filings', description: 'DOB NOW system filings', icon: '📄', color: 'blue' },
                  { id: 'dot_violations', name: 'DOT Violations', description: 'Department of Transportation violations', icon: '🚧', color: 'orange' },
                  { id: 'bsa_applications', name: 'BSA Applications', description: 'Board of Standards and Appeals', icon: '⚖️', color: 'purple' },
                ].map((dataset) => {
                  const datasetSettings = settings?.dataset_schedules?.[dataset.id] || {};
                  return (
                    <tr key={dataset.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{dataset.icon}</span>
                          <span className="font-medium text-white">{dataset.name}</span>
                        </div>
                      </td>
                      <td className="text-sm text-zinc-400">{dataset.description}</td>
                      <td>
                        <Select 
                          value={String(datasetSettings.frequency || settings?.data_sync_frequency || 24)}
                          onValueChange={(v) => {
                            const newSchedules = {
                              ...(settings?.dataset_schedules || {}),
                              [dataset.id]: {
                                ...(settings?.dataset_schedules?.[dataset.id] || {}),
                                frequency: parseInt(v)
                              }
                            };
                            handleUpdateSettings('dataset_schedules', newSchedules);
                          }}
                        >
                          <SelectTrigger className="w-36 input-dark h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Every Hour</SelectItem>
                            <SelectItem value="3">Every 3 Hours</SelectItem>
                            <SelectItem value="6">Every 6 Hours</SelectItem>
                            <SelectItem value="12">Every 12 Hours</SelectItem>
                            <SelectItem value="24">Daily</SelectItem>
                            <SelectItem value="48">Every 2 Days</SelectItem>
                            <SelectItem value="168">Weekly</SelectItem>
                            <SelectItem value="0">Disabled</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={datasetSettings.enabled !== false}
                            onCheckedChange={(v) => {
                              const newSchedules = {
                                ...(settings?.dataset_schedules || {}),
                                [dataset.id]: {
                                  ...(settings?.dataset_schedules?.[dataset.id] || {}),
                                  enabled: v
                                }
                              };
                              handleUpdateSettings('dataset_schedules', newSchedules);
                            }}
                          />
                          <span className={`text-xs ${datasetSettings.enabled !== false ? 'text-emerald-400' : 'text-zinc-500'}`}>
                            {datasetSettings.enabled !== false ? 'Active' : 'Paused'}
                          </span>
                        </div>
                      </td>
                      <td className="text-xs text-zinc-500">
                        {datasetSettings.last_sync 
                          ? new Date(datasetSettings.last_sync).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await api().post(`/admin/sync?dataset=${dataset.id}`);
                              toast.success(`Syncing ${dataset.name}...`);
                            } catch (err) {
                              toast.error('Sync failed');
                            }
                          }}
                          className="h-7 px-2 text-xs text-indigo-400 hover:text-indigo-300"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Sync Now
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sync Priority Info */}
          <div className="mt-4 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
            <p className="text-xs text-indigo-300">
              <strong>Note:</strong> Higher frequency datasets (hourly) consume more API quota. 
              Recommended: Set critical datasets (DOB, ECB violations) to hourly, others to daily.
            </p>
          </div>
        </div>

        {/* Email Testing */}
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <Mail className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Email Configuration</h2>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label className="text-zinc-300">Test Email Address</Label>
              <Input
                type="email"
                placeholder="Enter email to send test"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="input-dark mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleSendTestEmail}
                disabled={testEmailLoading || !testEmail}
                className="btn-primary"
              >
                {testEmailLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Test Email
              </Button>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            Send a test email to verify SendGrid is configured correctly. Requires SENDGRID_API_KEY in environment.
          </p>
        </div>

        {/* Sync Logs - Collapsible */}
        <div className="glass-card rounded-xl overflow-hidden">
          <button 
            onClick={() => setSyncHistoryOpen(!syncHistoryOpen)}
            className="w-full p-6 border-b border-white/10 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-400" />
              <h2 className="text-lg font-semibold text-white">Sync History</h2>
              <span className="text-xs text-zinc-500 ml-2">({syncLogs.length} logs)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={(e) => { e.stopPropagation(); handleRefreshLogs(); }}
                disabled={syncLogsLoading}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
              >
                {syncLogsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
              {syncHistoryOpen ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
            </div>
          </button>

          {syncHistoryOpen && (
            <>
              {syncLogs.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No sync logs yet. Trigger a sync to see history.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Started</th>
                        <th>Duration</th>
                        <th>Properties</th>
                        <th>Violations</th>
                        <th>Errors</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncLogs.map((log) => (
                        <tr key={log.id}>
                          <td>
                            {log.status === 'completed' ? (
                              <span className="flex items-center gap-1 text-emerald-400">
                                <CheckCircle className="w-4 h-4" />
                                Completed
                              </span>
                            ) : log.status === 'completed_with_errors' ? (
                              <span className="flex items-center gap-1 text-amber-400">
                                <AlertTriangle className="w-4 h-4" />
                                With Errors
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-red-400">
                                <XCircle className="w-4 h-4" />
                                Failed
                              </span>
                            )}
                          </td>
                          <td className="text-zinc-300">
                            {new Date(log.started_at).toLocaleString()}
                          </td>
                          <td className="text-zinc-400">
                            {log.duration_seconds ? `${log.duration_seconds}s` : '-'}
                          </td>
                          <td className="text-white font-medium">
                            {log.total_properties || 0}
                          </td>
                          <td>
                            <span className="badge-info">{log.violations_found || 0}</span>
                          </td>
                          <td>
                            {log.errors?.length > 0 ? (
                              <span className="badge-error">{log.errors.length}</span>
                            ) : (
                              <span className="text-zinc-500">0</span>
                            )}
                          </td>
                          <td>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewLogDetails(log.id)}
                              className="text-indigo-400 hover:text-indigo-300"
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Log Details Dialog */}
        <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
          <DialogContent className="glass-card border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-400" />
                Sync Log Details
              </DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4 mt-4">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-zinc-500">Started</p>
                    <p className="text-white">{new Date(selectedLog.started_at).toLocaleString()}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-zinc-500">Duration</p>
                    <p className="text-white">{selectedLog.duration_seconds}s</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-zinc-500">Watchlists</p>
                    <p className="text-white">{selectedLog.total_watchlists}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-zinc-500">Datasets Checked</p>
                    <p className="text-white">{selectedLog.datasets_checked}</p>
                  </div>
                </div>

                {/* Property Details */}
                {selectedLog.property_details?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-zinc-300 mb-2">Properties Synced</h4>
                    <div className="bg-white/5 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left p-2 text-zinc-400">Address</th>
                            <th className="text-left p-2 text-zinc-400">Block</th>
                            <th className="text-left p-2 text-zinc-400">Lot</th>
                            <th className="text-right p-2 text-zinc-400">Violations</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedLog.property_details.map((prop, i) => (
                            <tr key={i} className="border-b border-white/5">
                              <td className="p-2 text-white">{prop.address}</td>
                              <td className="p-2 text-zinc-400">{prop.block}</td>
                              <td className="p-2 text-zinc-400">{prop.lot}</td>
                              <td className="p-2 text-right">
                                <span className={prop.violations_found > 0 ? 'text-amber-400' : 'text-zinc-500'}>
                                  {prop.violations_found}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Errors */}
                {selectedLog.errors?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-red-400 mb-2">Errors ({selectedLog.errors.length})</h4>
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 space-y-1">
                      {selectedLog.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-300">{err}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* CRM Connections - Collapsible */}
        <div className="glass-card rounded-xl overflow-hidden" data-testid="crm-connections-section">
          <button 
            onClick={() => setCrmConnectionsOpen(!crmConnectionsOpen)}
            className="w-full p-6 border-b border-white/10 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-semibold text-white">CRM Connections</h2>
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full ml-2">
                {crmConnections.length} connection{crmConnections.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={(e) => { e.stopPropagation(); handleRefreshCrmConnections(); }}
                disabled={crmLoading}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
                data-testid="refresh-crm-btn"
              >
                {crmLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
              {crmConnectionsOpen ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
            </div>
          </button>

          {crmConnectionsOpen && (
            <>
              {crmConnections.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <Link2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No CRM connections configured yet.</p>
                  <p className="text-xs mt-2">Users can connect their CRM from the Settings page.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="crm-connections-table">
                    <thead>
                      <tr>
                        <th>CRM Type</th>
                        <th>User</th>
                        <th>Instance URL</th>
                        <th>Status</th>
                        <th>Last Sync</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crmConnections.map((conn) => (
                        <tr key={conn.id} data-testid={`crm-row-${conn.id}`}>
                          <td>
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{getCrmIcon(conn.crm_type)}</span>
                              <span className="font-medium text-white capitalize">{conn.crm_type}</span>
                            </div>
                          </td>
                          <td>
                            <div>
                              <p className="text-white text-sm">{conn.user_name}</p>
                              <p className="text-xs text-zinc-500">{conn.user_email}</p>
                            </div>
                          </td>
                          <td className="text-zinc-400 text-xs max-w-[200px] truncate" title={conn.instance_url}>
                            {conn.instance_url || '-'}
                          </td>
                          <td>
                            <div className="flex items-center gap-1.5">
                              {conn.status === 'connected' ? (
                                <CheckCircle className="w-4 h-4 text-emerald-400" />
                              ) : conn.status === 'error' ? (
                                <XCircle className="w-4 h-4 text-red-400" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-amber-400" />
                              )}
                              <span className={`text-sm capitalize ${getCrmStatusColor(conn.status)}`}>
                                {conn.status}
                              </span>
                            </div>
                          </td>
                          <td className="text-zinc-400 text-sm">
                            {conn.last_sync 
                              ? new Date(conn.last_sync).toLocaleDateString() 
                              : 'Never'}
                          </td>
                          <td className="text-zinc-500 text-xs">
                            {new Date(conn.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleTestCrmConnection(conn.id)}
                                disabled={testingConnection === conn.id}
                                className="text-blue-400 hover:text-blue-300 p-1"
                                title="Test Connection"
                                data-testid={`test-crm-${conn.id}`}
                              >
                                {testingConnection === conn.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <TestTube className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSyncCrmConnection(conn.id)}
                                disabled={syncingConnection === conn.id}
                                className="text-emerald-400 hover:text-emerald-300 p-1"
                                title="Sync Data"
                                data-testid={`sync-crm-${conn.id}`}
                              >
                                {syncingConnection === conn.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCrmConnection(conn.id, conn.crm_type)}
                                className="text-red-400 hover:text-red-300 p-1"
                                title="Delete Connection"
                                data-testid={`delete-crm-${conn.id}`}
                              >
                                <Unlink className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Teams Management - Collapsible */}
        <div className="glass-card rounded-xl overflow-hidden" data-testid="teams-section">
          <button 
            onClick={() => setTeamsOpen(!teamsOpen)}
            className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-indigo-400" />
              <h2 className="text-lg font-semibold text-white">Teams Management</h2>
              {teamsStats && (
                <span className="text-xs text-zinc-500 bg-white/10 px-2 py-0.5 rounded-full">
                  {teamsStats.total_teams} teams
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              {teamsLoading && <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />}
              {teamsOpen ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
            </div>
          </button>
          
          {teamsOpen && (
            <>
              {/* Teams Stats */}
              {teamsStats && (
                <div className="px-6 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-2xl font-bold text-white">{teamsStats.total_teams}</div>
                      <div className="text-xs text-zinc-500">Total Teams</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-2xl font-bold text-white">{teamsStats.total_memberships}</div>
                      <div className="text-xs text-zinc-500">Total Memberships</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-2xl font-bold text-white">{teamsStats.total_shared_resources}</div>
                      <div className="text-xs text-zinc-500">Shared Resources</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-2xl font-bold text-white">{teamsStats.average_team_size || 0}</div>
                      <div className="text-xs text-zinc-500">Avg Team Size</div>
                    </div>
                  </div>
                  
                  {/* Teams Table */}
                  {allTeams.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th className="text-left p-3 text-zinc-400 text-xs font-medium">Team Name</th>
                            <th className="text-left p-3 text-zinc-400 text-xs font-medium">Owner</th>
                            <th className="text-left p-3 text-zinc-400 text-xs font-medium">Members</th>
                            <th className="text-left p-3 text-zinc-400 text-xs font-medium">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allTeams.map((team) => (
                            <tr key={team.id} className="border-t border-white/5">
                              <td className="p-3">
                                <div className="font-medium text-white">{team.name}</div>
                                {team.description && (
                                  <div className="text-xs text-zinc-500 truncate max-w-[200px]">{team.description}</div>
                                )}
                              </td>
                              <td className="p-3 text-sm text-zinc-400">{team.owner_email}</td>
                              <td className="p-3 text-sm text-zinc-400">{team.member_count}</td>
                              <td className="p-3 text-sm text-zinc-500">
                                {new Date(team.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {allTeams.length === 0 && !teamsLoading && (
                    <div className="text-center py-8 text-zinc-500">
                      No teams created yet
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Security Dashboard */}
        <div className="glass-card rounded-xl p-6" data-testid="security-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-rose-400" />
              <h2 className="text-lg font-semibold text-white">Security Dashboard</h2>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { fetchAuditLogs(); setAuditLogsOpen(true); }}
              className="text-zinc-400 hover:text-white"
            >
              <Eye className="w-4 h-4 mr-2" />
              View All Logs
            </Button>
          </div>

          {/* Security Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-zinc-500">Events Today</span>
              </div>
              <p className="text-2xl font-bold text-white">{auditStats?.events_today || 0}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-rose-400" />
                <span className="text-xs text-zinc-500">Failed Logins (24h)</span>
              </div>
              <p className="text-2xl font-bold text-white">{auditStats?.failed_logins_24h || 0}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-zinc-500">Rate Limit</span>
              </div>
              <p className="text-lg font-bold text-white">5/min login</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-4 h-4 text-indigo-400" />
                <span className="text-xs text-zinc-500">Total Logs</span>
              </div>
              <p className="text-2xl font-bold text-white">{auditStats?.total_logs || 0}</p>
            </div>
          </div>

          {/* Event Type Breakdown */}
          {auditStats?.event_counts && auditStats.event_counts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Event Breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {auditStats.event_counts.slice(0, 8).map((event, i) => (
                  <div key={i} className="flex items-center justify-between bg-white/5 rounded px-3 py-2">
                    <span className={`text-xs ${event.event_type?.includes('FAILED') ? 'text-rose-400' : 'text-zinc-300'}`}>
                      {event.event_type?.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs font-medium text-white">{event.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PDF Downloads - General Documents */}
          <div className="mt-6 pt-4 border-t border-white/10 space-y-3">
            <h4 className="text-sm font-medium text-zinc-400 mb-2">Documents</h4>
            <a 
              href={`${process.env.REACT_APP_BACKEND_URL}/api/export/executive-summary?token=${localStorage.getItem('token')}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-teal-400 hover:text-teal-300 transition-colors"
              data-testid="exec-summary-link"
            >
              <FileText className="w-4 h-4" />
              Executive Summary PDF (Jan 27-29, 2026)
            </a>
            <a 
              href={`${process.env.REACT_APP_BACKEND_URL}/api/export/testing-outline?token=${localStorage.getItem('token')}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 transition-colors"
              data-testid="testing-outline-link"
            >
              <FileText className="w-4 h-4" />
              Testing Outline & QA Guide
            </a>
            <a 
              href="/Executive_Summary_NYC_Compliance_Track.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Executive Summary - NYC Compliance Track
            </a>
            <a 
              href="/Security_Hardening_Report.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Security Recommendations
            </a>
            <a 
              href="/UX_Analysis_Report.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-pink-400 hover:text-pink-300 transition-colors"
              data-testid="ux-analysis-link"
            >
              <FileText className="w-4 h-4" />
              UX Analysis Report
            </a>
          </div>
        </div>

        {/* Error/Fix Log - Collapsible */}
        <div className="glass-card rounded-xl overflow-hidden" data-testid="error-fix-log-section">
          <button 
            onClick={() => setErrorLogOpen(!errorLogOpen)}
            className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Bug className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-white">Error/Fix Log</h2>
              <span className="text-xs text-zinc-500 bg-white/10 px-2 py-0.5 rounded-full">
                Development Log
              </span>
            </div>
            <div className="flex items-center gap-4">
              {errorLogLoading && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
              <Button
                onClick={(e) => { e.stopPropagation(); fetchErrorLog(); }}
                disabled={errorLogLoading}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
              >
                <RefreshCw className={`w-4 h-4 ${errorLogLoading ? 'animate-spin' : ''}`} />
              </Button>
              {errorLogOpen ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
            </div>
          </button>
          
          {errorLogOpen && (
            <div className="px-6 pb-6">
              {errorLogLoading && !errorLogContent ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                </div>
              ) : errorLogContent ? (
                <div className="space-y-4">
                  {/* Last modified timestamp */}
                  {errorLogContent.last_modified && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Clock className="w-3 h-3" />
                      Last updated: {new Date(errorLogContent.last_modified).toLocaleString()}
                    </div>
                  )}
                  
                  {/* Log content rendered as markdown-like sections */}
                  <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-4 overflow-auto max-h-[600px]">
                    <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">
                      {errorLogContent.content?.split('\n').map((line, idx) => {
                        // Style headers
                        if (line.startsWith('# ')) {
                          return <div key={idx} className="text-xl font-bold text-white mt-4 mb-2 border-b border-white/10 pb-2">{line.replace('# ', '')}</div>;
                        }
                        if (line.startsWith('## ')) {
                          return <div key={idx} className="text-lg font-semibold text-amber-400 mt-6 mb-2">{line.replace('## ', '')}</div>;
                        }
                        if (line.startsWith('### ')) {
                          return <div key={idx} className="text-base font-medium text-cyan-400 mt-4 mb-1">{line.replace('### ', '')}</div>;
                        }
                        // Style status badges
                        if (line.includes('**Status:**')) {
                          const statusMatch = line.match(/\*\*Status:\*\*\s*(\w+)/);
                          const status = statusMatch ? statusMatch[1] : '';
                          const statusColor = status === 'FIXED' ? 'text-emerald-400' : status === 'NEEDS' ? 'text-amber-400' : 'text-zinc-400';
                          return (
                            <div key={idx} className="flex items-center gap-2 my-1">
                              <span className="text-zinc-400">Status:</span>
                              <span className={`font-semibold ${statusColor}`}>{status}</span>
                              {status === 'FIXED' && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                            </div>
                          );
                        }
                        // Style list items
                        if (line.startsWith('- ')) {
                          return <div key={idx} className="text-zinc-300 pl-4 my-0.5">• {line.substring(2)}</div>;
                        }
                        // Regular lines
                        if (line.trim() === '---') {
                          return <hr key={idx} className="border-white/10 my-4" />;
                        }
                        return <div key={idx} className="text-zinc-400">{line}</div>;
                      })}
                    </pre>
                  </div>
                  
                  {/* Quick summary */}
                  <div className="flex items-center gap-4 text-xs text-zinc-500 pt-2 border-t border-white/10">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-emerald-400" />
                      {(errorLogContent.content?.match(/Status:\*\*\s*FIXED/g) || []).length} Fixed
                    </span>
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-amber-400" />
                      {(errorLogContent.content?.match(/Status:\*\*\s*NEEDS/g) || []).length} Pending
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <Bug className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No error/fix log available yet</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sales Tools Section - JH Only */}
        {isSalesAdmin && (
          <div className="glass-card rounded-xl p-6" data-testid="sales-tools-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-rose-500/20 rounded-lg flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-rose-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Sales Tools</h2>
                <p className="text-xs text-zinc-500">Pitch decks, email templates & outreach materials</p>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/export/sales-materials-bundle?token=${localStorage.getItem('token')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white text-sm font-medium rounded-lg transition-all shadow-lg shadow-rose-500/25"
                data-testid="download-all-materials"
              >
                <Download className="w-4 h-4" />
                Download All (ZIP)
              </a>
              <span className="flex items-center gap-1 text-xs text-rose-400 bg-rose-500/10 px-2 py-1 rounded-full">
                <Lock className="w-3 h-3" />
                JH Only
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* GTM Plan */}
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/export/gtm-plan?token=${localStorage.getItem('token')}&budget=5000`}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-green-500/50 hover:bg-green-500/5 transition-all group"
                data-testid="gtm-plan-link"
              >
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white group-hover:text-green-300">GTM Plan ($5K)</p>
                  <p className="text-xs text-zinc-500">30-day launch strategy</p>
                </div>
              </a>
              
              {/* Portfolio Owners Report */}
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/export/portfolio-owners?token=${localStorage.getItem('token')}&min_properties=10`}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-teal-500/50 hover:bg-teal-500/5 transition-all group"
                data-testid="portfolio-owners-link"
              >
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white group-hover:text-teal-300">Portfolio Owners</p>
                  <p className="text-xs text-zinc-500">NYC owners with 10+ properties</p>
                </div>
              </a>
              
              {/* Pitch Deck */}
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/export/pitch-deck?token=${localStorage.getItem('token')}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-blue-500/50 hover:bg-blue-500/5 transition-all group"
                data-testid="pitch-deck-link"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white group-hover:text-blue-300">Sales Pitch Deck</p>
                  <p className="text-xs text-zinc-500">6-slide presentation for prospects</p>
                </div>
              </a>
              
              {/* Cold Email Templates */}
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/export/cold-email-templates?token=${localStorage.getItem('token')}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-rose-500/50 hover:bg-rose-500/5 transition-all group"
                data-testid="cold-email-link"
              >
                <div className="w-10 h-10 rounded-lg bg-rose-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-rose-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white group-hover:text-rose-300">Cold Email Templates</p>
                  <p className="text-xs text-zinc-500">15-sec pitches & email scripts</p>
                </div>
              </a>
              
              {/* Competitor Analysis */}
              <a 
                href="/Competitor_Analysis.pdf"
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-orange-500/50 hover:bg-orange-500/5 transition-all group"
                data-testid="competitor-analysis-link"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white group-hover:text-orange-300">Competitor Analysis</p>
                  <p className="text-xs text-zinc-500">Pricing & market positioning</p>
                </div>
              </a>
              
              {/* Demo Script */}
              <a 
                href="/Demo_Script.pdf"
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-all group"
                data-testid="demo-script-link"
              >
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white group-hover:text-cyan-300">Prospect Demo Script</p>
                  <p className="text-xs text-zinc-500">Step-by-step demo walkthrough</p>
                </div>
              </a>
            </div>
            
            {/* Quick Reference Pitches */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <h4 className="text-sm font-medium text-zinc-400 mb-3">Quick Reference: 15-Second Pitches</h4>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-white/5 border-l-2 border-blue-500">
                  <p className="text-xs text-blue-400 font-medium mb-1">Building Owners & Managers</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">"We help building owners track violations across DOB, HPD, ECB, and FDNY in one dashboard. Insite360 monitors your portfolio 24/7 and alerts you before violations become fines. Quick demo?"</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 border-l-2 border-teal-500">
                  <p className="text-xs text-teal-400 font-medium mb-1">Compliance Managers</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">"Insite360 gives compliance teams a single view of 41 million violation records across 12 NYC agencies. Auto-alerts, watchlists, and one-click reports. Can I show you?"</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 border-l-2 border-amber-500">
                  <p className="text-xs text-amber-400 font-medium mb-1">Developers</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">"Before you close on a deal, Insite360 shows you every open violation and permit filing instantly. Due diligence in minutes, not days. Worth a quick look?"</p>
                </div>
              </div>
            </div>
            
            {/* Recycle Bin - Outdated Documents */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <h4 className="text-sm font-medium text-zinc-500 mb-3 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                Recycle Bin (Outdated)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <a 
                  href="/Insite360_Deck.pdf"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50 hover:border-zinc-600/50 transition-all group opacity-60 hover:opacity-80"
                  data-testid="archived-deck-link"
                >
                  <div className="w-8 h-8 rounded-lg bg-zinc-700/50 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-zinc-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-400 group-hover:text-zinc-300">Insite360_Deck.pdf</p>
                    <p className="text-xs text-zinc-600">Old version - replaced by Sales Pitch Deck</p>
                  </div>
                </a>
                <a 
                  href="/Insite360_SlideDeck.pdf"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50 hover:border-zinc-600/50 transition-all group opacity-60 hover:opacity-80"
                  data-testid="archived-slidedeck-link"
                >
                  <div className="w-8 h-8 rounded-lg bg-zinc-700/50 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-zinc-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-400 group-hover:text-zinc-300">Insite360_SlideDeck.pdf</p>
                    <p className="text-xs text-zinc-600">Old version - replaced by Sales Pitch Deck</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* User Sign-On Log Section */}
        <div className="glass-card rounded-xl p-6" data-testid="sign-on-log-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">User Sign-On Log</h2>
                <p className="text-xs text-zinc-500">Track all user login activity</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={signOnFilter} onValueChange={(val) => setSignOnFilter(val)}>
                <SelectTrigger className="w-32 bg-white/5 border-white/10 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="all">All Logins</SelectItem>
                  <SelectItem value="success">Successful</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => fetchSignOnLogs(signOnFilter)}
                disabled={signOnLogsLoading}
                className="border-white/10 text-zinc-300 hover:text-white"
              >
                {signOnLogsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          {/* Sign-On Stats Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">
                {signOnLogs.filter(l => l.event_type === 'LOGIN_SUCCESS').length}
              </p>
              <p className="text-xs text-zinc-500 mt-1">Successful</p>
            </div>
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-rose-400">
                {signOnLogs.filter(l => l.event_type === 'LOGIN_FAILED').length}
              </p>
              <p className="text-xs text-zinc-500 mt-1">Failed</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">
                {signOnLogs.filter(l => l.event_type === 'LOGIN_BLOCKED').length}
              </p>
              <p className="text-xs text-zinc-500 mt-1">Blocked</p>
            </div>
          </div>

          {/* Sign-On Log Table */}
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-zinc-900">
                <tr className="text-left text-xs text-zinc-500 uppercase border-b border-white/10">
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">User</th>
                  <th className="py-3 px-4">IP Address</th>
                  <th className="py-3 px-4">Device/Browser</th>
                  <th className="py-3 px-4">Time</th>
                  <th className="py-3 px-4">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {signOnLogsLoading ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-zinc-500 mx-auto" />
                    </td>
                  </tr>
                ) : signOnLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-zinc-500">
                      No sign-on logs found
                    </td>
                  </tr>
                ) : (
                  signOnLogs.map((log, i) => (
                    <tr key={i} className="hover:bg-white/5 transition-colors">
                      <td className="py-3 px-4">
                        {log.event_type === 'LOGIN_SUCCESS' ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            Success
                          </span>
                        ) : log.event_type === 'LOGIN_BLOCKED' ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400">
                            <Lock className="w-3 h-3" />
                            Blocked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-rose-500/20 text-rose-400">
                            <XCircle className="w-3 h-3" />
                            Failed
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-white">{log.user_email || 'Unknown'}</div>
                        {log.details?.tier && (
                          <div className="text-xs text-zinc-500">{log.details.tier} tier</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-zinc-400 font-mono text-xs">
                        {log.ip_address || 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-zinc-400 text-xs max-w-[200px] truncate" title={log.user_agent}>
                        {log.user_agent ? (
                          log.user_agent.includes('Chrome') ? 'Chrome' :
                          log.user_agent.includes('Firefox') ? 'Firefox' :
                          log.user_agent.includes('Safari') ? 'Safari' :
                          log.user_agent.includes('Edge') ? 'Edge' :
                          log.user_agent.substring(0, 30) + '...'
                        ) : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-zinc-400 text-xs">
                        <div>{new Date(log.timestamp).toLocaleDateString()}</div>
                        <div className="text-zinc-500">{new Date(log.timestamp).toLocaleTimeString()}</div>
                      </td>
                      <td className="py-3 px-4 text-xs">
                        {log.details?.reason ? (
                          <span className="text-rose-400">{log.details.reason}</span>
                        ) : log.details?.role ? (
                          <span className="text-zinc-400">Role: {log.details.role}</span>
                        ) : (
                          <span className="text-zinc-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Audit Logs Dialog */}
        <Dialog open={auditLogsOpen} onOpenChange={setAuditLogsOpen}>
          <DialogContent className="glass-card border-white/10 max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-rose-400" />
                Audit Logs
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 mt-4">
              {auditLogs.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">No audit logs found</p>
              ) : (
                auditLogs.map((log, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-medium ${log.event_type?.includes('FAILED') ? 'text-rose-400' : 'text-emerald-400'}`}>
                        {log.event_type}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-400">
                      <span>{log.user_email || 'Anonymous'}</span>
                      <span className="mx-2">•</span>
                      <span>{log.ip_address}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Scheduler Health Monitor */}
        <div className="glass-card rounded-xl p-6" data-testid="scheduler-health-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Scheduler Health</h2>
                <p className="text-xs text-zinc-500">Monitor background job status</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {schedulerHealth && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  schedulerHealth.overall_health === 'healthy' 
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                }`}>
                  {schedulerHealth.overall_health === 'healthy' ? '● Healthy' : '⚠ Degraded'}
                </span>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchSchedulerHealth}
                disabled={schedulerLoading}
                className="border-white/10 text-zinc-300 hover:text-white"
              >
                {schedulerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          {/* Warnings */}
          {schedulerHealth?.warnings?.length > 0 && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-sm font-medium text-amber-400 mb-2">⚠ Warnings</p>
              {schedulerHealth.warnings.map((warning, i) => (
                <p key={i} className="text-xs text-amber-300">{warning}</p>
              ))}
            </div>
          )}
          
          {/* Scheduler Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {schedulerHealth?.schedulers && Object.entries(schedulerHealth.schedulers).map(([key, scheduler]) => (
              <div 
                key={key} 
                className={`rounded-lg p-4 border ${
                  scheduler.healthy 
                    ? 'bg-emerald-500/5 border-emerald-500/20'
                    : 'bg-rose-500/5 border-rose-500/20'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white">{scheduler.name}</span>
                  {scheduler.healthy ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-rose-400" />
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Status</span>
                    <span className={`${scheduler.enabled ? 'text-emerald-400' : 'text-zinc-500'}`}>
                      {scheduler.status || (scheduler.enabled ? 'running' : 'disabled')}
                    </span>
                  </div>
                  {scheduler.hours_since_last_run !== null && scheduler.hours_since_last_run !== undefined && (
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">Last Run</span>
                      <span className="text-zinc-300">{scheduler.hours_since_last_run}h ago</span>
                    </div>
                  )}
                  {scheduler.seconds_since_last_run !== null && scheduler.seconds_since_last_run !== undefined && (
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">Last Run</span>
                      <span className="text-zinc-300">{Math.round(scheduler.seconds_since_last_run / 60)}m ago</span>
                    </div>
                  )}
                  {scheduler.pending_reports !== undefined && (
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">Pending</span>
                      <span className="text-zinc-300">{scheduler.pending_reports}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Recent Runs */}
          {schedulerHealth?.recent_runs?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-zinc-400 mb-3">Recent Runs</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500">Scheduler</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500">Status</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500">Duration</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedulerHealth.recent_runs.slice(0, 5).map((run, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-2 px-3 text-zinc-300">{run.scheduler_name}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            run.status === 'success' 
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : run.status === 'error'
                              ? 'bg-rose-500/20 text-rose-400'
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {run.status}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-zinc-400">
                          {run.duration_seconds ? `${run.duration_seconds.toFixed(1)}s` : '-'}
                        </td>
                        <td className="py-2 px-3 text-zinc-500 text-xs">
                          {new Date(run.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {!schedulerHealth && !schedulerLoading && (
            <p className="text-center text-zinc-500 py-8">Click refresh to load scheduler health data</p>
          )}
        </div>

        {/* Communication Templates */}
        <div className="glass-card rounded-xl p-6" data-testid="templates-section">
          <TemplateBuilder 
            onSave={(templates) => {
              console.log('Templates saved:', templates);
              // In production, save to backend
            }}
          />
        </div>

        {/* Users Table */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h2 className="text-lg font-semibold text-white">User Management</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Tier</th>
                  <th>Datasets</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="font-medium text-white">{u.name}</td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`badge-${u.role === 'admin' ? 'warning' : 'info'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      <span className="badge-success capitalize">{u.tier}</span>
                    </td>
                    <td className="text-xs">{u.datasets_access?.length || 0} datasets</td>
                    <td>
                      <Dialog open={userDialogOpen && selectedUser?.id === u.id} onOpenChange={(open) => {
                        setUserDialogOpen(open);
                        if (open) {
                          setSelectedUser(u);
                          setNewTier(u.tier);
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-indigo-400 hover:text-indigo-300">
                            Edit Tier
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="glass-card border-white/10">
                          <DialogHeader>
                            <DialogTitle className="text-white">Update User Tier</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <p className="text-zinc-400">
                              Changing tier for: <span className="text-white">{selectedUser?.email}</span>
                            </p>
                            <div>
                              <Label className="text-zinc-300">New Tier</Label>
                              <Select value={newTier} onValueChange={setNewTier}>
                                <SelectTrigger className="input-dark mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="free">Free</SelectItem>
                                  <SelectItem value="basic">Basic</SelectItem>
                                  <SelectItem value="pro">Pro</SelectItem>
                                  <SelectItem value="enterprise">Enterprise</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex justify-end gap-3">
                              <Button variant="ghost" onClick={() => setUserDialogOpen(false)} className="btn-ghost">
                                Cancel
                              </Button>
                              <Button onClick={handleUpdateTier} className="btn-primary">
                                Update
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
